// @ts-nocheck
/*
 * MarketplacePage — dynamic catalog. Fetches active solutions from
 * /api/marketplace/solutions instead of the old hardcoded JSX. Super-users
 * can grow the catalog via POST /api/marketplace/solutions; new cards
 * appear here automatically.
 *
 * Each solution renders as a tile with: icon (matched to solution_type),
 * status badge, name, tagline, channel pills, and an "Open" CTA. The
 * chatbot slug routes through the Phase 5 first-touch flow
 * (prepare → /marketplace/chatbot/preparing → /marketplace/chatbot).
 * Where each slug opens comes from lib/marketplaceSolutions (SOLUTION_PAGES);
 * a slug without a page there renders as a "Coming soon" card that does not
 * open, until its page ships.
 *
 * Empty state: when fewer than 3 solutions exist, fill the remaining
 * slots in the row with the "More coming soon" dashed-border placeholder
 * so the grid never looks empty.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MessageCircle, ArrowRight, Sparkles, Globe, CheckCircle, Loader2,
  ShoppingBag, Hammer, Workflow, Wrench, Cpu, GraduationCap,
} from 'lucide-react';
import { getClientProfile } from '../services/api';
import { solutionPage, isExternalUrl } from '../lib/marketplaceSolutions';

const API_BASE = process.env.REACT_APP_API_URL ?? '';


// Map solution_type → icon. Falls back to Sparkles for unknown types.
const TYPE_ICON: Record<string, React.ComponentType<any>> = {
  chatbot: MessageCircle,
  agent: Cpu,
  workflow: Workflow,
  integration: Wrench,
  saas: ShoppingBag,
  training: GraduationCap,
};

// Map channel slug → icon for the channel pills inside each card.
const CHANNEL_ICON: Record<string, React.ComponentType<any>> = {
  website: Globe,
  whatsapp: MessageCircle,
  slack: MessageCircle,
  teams: MessageCircle,
  sms: MessageCircle,
  email: MessageCircle,
  voice: MessageCircle,
  api: Hammer,
};

const STATUS_BADGE: Record<string, { label: string; className: string; icon?: React.ComponentType<any> }> = {
  active:   { label: 'Ready',     className: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: CheckCircle },
  beta:     { label: 'Beta',      className: 'bg-amber-50 text-amber-700 ring-amber-200',       icon: Sparkles },
  preview:  { label: 'Preview',   className: 'bg-brand-50 text-brand-700 ring-brand-200',       icon: Sparkles },
  inactive: { label: 'Inactive',  className: 'bg-gray-100 text-gray-500 ring-gray-200' },
};

const COMING_SOON_BADGE = { label: 'Coming soon', className: 'bg-gray-100 text-gray-500 ring-gray-200' };

// A card opens when this app has a page for it (SOLUTION_PAGES, shared with the
// /marketplace/:slug route) or its landing_url is on another site. Anything
// else reads "Coming soon" and stays closed instead of opening a blank screen.
const canOpen = (solution: Solution): boolean =>
  !!solutionPage(solution.slug) || isExternalUrl(solution.landing_url);


interface Solution {
  slug: string;
  name: string;
  tagline?: string;
  description?: string;
  solution_type?: string;
  channels?: string[];
  pricing_tier?: string;
  status?: string;
  landing_url?: string;
}


const MarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem('client_id') || localStorage.getItem('impersonated_client_id');
  const [profile, setProfile] = useState<any>(null);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingSlug, setOpeningSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        // Profile drives the brand-color tint on each card; non-fatal.
        if (clientId) {
          try {
            const data = await getClientProfile(clientId);
            if (!cancelled) setProfile(data);
          } catch (e) {
            console.error('Profile load failed (non-fatal):', e);
          }
        }
        const res = await axios.get(`${API_BASE}/api/marketplace/solutions`);
        if (!cancelled) setSolutions((res.data?.solutions || []) as Solution[]);
      } catch (err) {
        console.error('Failed to load marketplace solutions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [clientId]);

  const brandColor = profile?.branding?.primary_color || '#4080F2';

  // Chatbot slug owns the Phase 5 first-touch flow (prepare → progress
  // → detail). Every other slug opens its page from SOLUTION_PAGES in the
  // same tab. Only a landing_url on another site opens in a new tab: the
  // catalog seeds in-app paths here, and opening those in a new tab skipped
  // the chatbot's prepare.
  const handleOpenSolution = async (solution: Solution) => {
    if (openingSlug || !canOpen(solution)) return;
    if (isExternalUrl(solution.landing_url)) {
      window.open(solution.landing_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (solution.slug === 'chatbot') {
      setOpeningSlug(solution.slug);
      try {
        const { data } = await axios.post(`${API_BASE}/api/marketplace/chatbot/prepare`);
        if (data?.already_prepared || data?.status === 'completed') {
          navigate('/marketplace/chatbot');
        } else {
          navigate('/marketplace/chatbot/preparing');
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        const reason = (typeof detail === 'object' ? detail?.reason : null);
        if (reason === 'no_website') {
          navigate('/my-business#dossier');
        } else {
          navigate('/marketplace/chatbot');
        }
      } finally {
        setOpeningSlug(null);
      }
      return;
    }
    navigate(solutionPage(solution.slug));
  };

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Marketplace</h1>
        <p className="text-sm text-gray-500 mt-1">
          Solutions you can deploy on your stack.
        </p>
      </div>

      {/* Loading skeleton */}
      {loading && solutions.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse"
            >
              <div className="h-32 bg-gray-50" />
              <div className="p-5 space-y-3">
                <div className="h-4 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-full" />
                <div className="h-3 bg-gray-100 rounded w-5/6" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map(solution => {
            const Icon = TYPE_ICON[solution.solution_type || 'agent'] || Sparkles;
            const statusKey = (solution.status || 'active').toLowerCase();
            const openable = canOpen(solution);
            const badge = openable ? (STATUS_BADGE[statusKey] || STATUS_BADGE.active) : COMING_SOON_BADGE;
            const StatusIcon = badge.icon;
            const channels = (solution.channels || []).slice(0, 3);
            const isOpening = openingSlug === solution.slug;
            return (
              <div
                key={solution.slug}
                onClick={openable ? () => handleOpenSolution(solution) : undefined}
                aria-disabled={!openable}
                title={openable ? undefined : `${solution.name} is coming soon`}
                className={`group relative bg-white rounded-2xl border border-gray-200 shadow-sm transition-all duration-200 overflow-hidden ${
                  openable ? 'hover:shadow-lg hover:border-brand-200 cursor-pointer' : 'cursor-not-allowed opacity-70'
                }`}
              >
                {/* Status badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${badge.className}`}
                  >
                    {StatusIcon && <StatusIcon size={12} />}
                    {badge.label}
                  </span>
                </div>

                {/* Card header with brand-tinted gradient */}
                <div
                  className="h-32 flex items-center justify-center relative"
                  style={{
                    background: `linear-gradient(135deg, ${brandColor}15, ${brandColor}30)`,
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: `${brandColor}20` }}
                  >
                    <Icon size={32} style={{ color: brandColor }} />
                  </div>
                </div>

                {/* Card body */}
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {solution.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 leading-relaxed line-clamp-2">
                    {solution.tagline ||
                      solution.description?.slice(0, 120) ||
                      'Deploy on your stack.'}
                  </p>

                  {/* Channel pills */}
                  {channels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {channels.map(channel => {
                        const ChannelIcon = CHANNEL_ICON[channel] || Sparkles;
                        return (
                          <span
                            key={channel}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 text-gray-600 capitalize"
                          >
                            <ChannelIcon size={10} /> {channel}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* CTA */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    {openable ? (
                      <>
                        <span className="text-sm font-medium text-brand-600">
                          {isOpening ? 'Preparing…' : 'Open'}
                        </span>
                        {isOpening
                          ? <Loader2 size={16} className="text-brand-500 animate-spin" />
                          : <ArrowRight size={16} className="text-brand-500 group-hover:translate-x-1 transition-transform" />}
                      </>
                    ) : (
                      <span className="text-sm font-medium text-gray-400">Coming soon</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pad the grid with "Coming soon" placeholders so it never
              looks empty when only one or two solutions exist. */}
          {Array.from({ length: Math.max(0, 3 - solutions.length) }).map((_, i) => (
            <div
              key={`placeholder-${i}`}
              className="relative bg-white/60 rounded-2xl border border-dashed border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="h-32 flex items-center justify-center bg-gray-50/50">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <Sparkles size={32} className="text-gray-300" />
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-semibold text-gray-400 mb-1">More coming soon</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  New solutions appear here as the team ships them.
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;
